import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import {
  extractApiErrorMessage,
  stockComparisonService,
  StockComparisonMovementsResponse,
} from '../../../../core/modules/stockComparison/services/api';

interface Props {
  isOpen: boolean;
  companyId: number;
  productSku: string;
  productName: string | null;
  warehouseLabel: string;
  referenceDate: string;
  onClose: () => void;
}

const SOURCE_LABEL: Record<string, string> = {
  bsale: 'Bsale',
  skualo: 'Skualo',
  generic: 'Inventaria',
};

// Diagnostico ASISTIDO, no automatico: esto no intenta adivinar cual
// movimiento explica el descuadre -- solo trae la lista real del sistema
// primario para que el usuario la revise el mismo (decision de diseño ya
// acordada con Seba).
const StockComparisonMovementsDialog = ({
  isOpen,
  companyId,
  productSku,
  productName,
  warehouseLabel,
  referenceDate,
  onClose,
}: Props) => {
  const [data, setData] = useState<StockComparisonMovementsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    setData(null);
    stockComparisonService
      .getMovements(companyId, {
        productSku,
        warehouseLabel,
        referenceDate,
        daysBack: 30,
        daysForward: 1,
      })
      .then((result) => setData(result))
      .catch((err: any) => {
        setError(extractApiErrorMessage(err, 'No se pudieron cargar los movimientos.'));
      })
      .finally(() => setLoading(false));
  }, [isOpen, companyId, productSku, warehouseLabel, referenceDate]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Diagnóstico asistido</Typography>
          <Typography variant="body2" color="text.secondary">
            {productName ?? productSku} ({productSku}) · {warehouseLabel}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {data && !loading && !error && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Movimientos registrados en {data.source ? SOURCE_LABEL[data.source] ?? data.source : 'el sistema primario'}{' '}
              entre el {moment(data.rangeStart).format('DD/MM/YYYY')} y el{' '}
              {moment(data.rangeEnd).format('DD/MM/YYYY')}. Esta lista es de apoyo -- revisala para
              ubicar la transacción que probablemente generó el descuadre, no reemplaza tu criterio.
            </Typography>
            {data.source === null && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta empresa no tiene una fuente de movimientos reconocida (ni Bsale, ni Skualo, ni el
                registro genérico de Inventaria) -- no hay de dónde traer el detalle transaccional.
              </Alert>
            )}
            {data.source !== null && data.movements.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No se encontraron movimientos para este producto en esta bodega dentro del rango de
                fechas. La desviación podría venir de fuera de este rango, o de un ajuste que el
                sistema primario no registra como movimiento.
              </Alert>
            )}
            {data.movements.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell>Referencia</TableCell>
                      <TableCell>Notas</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.movements.map((m, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={`${m.movementDate}-${m.reference}-${idx}`}>
                        <TableCell>{moment(m.movementDate).format('DD/MM/YYYY')}</TableCell>
                        <TableCell>
                          <Chip label={m.typeLabel} size="small" />
                        </TableCell>
                        <TableCell align="right">{m.quantity === null ? '—' : Number(m.quantity).toLocaleString('es-CL')}</TableCell>
                        <TableCell>{m.reference || '—'}</TableCell>
                        <TableCell>{m.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StockComparisonMovementsDialog;
